import React, { useState } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import { agentSolidColour } from "../utils/agentColour";

// Single-line "send me a sample" pill: an email or phone input attached to a
// Send button, branded with the agency colour. Posts to one of the settings
// sample endpoints, authenticated by param_token. Used on the marketing
// landing page and inside the sticky branded-hub promo on the blog/site.

const SAMPLE_PROPERTY_ID = "vl3272ep";
const SAMPLE_PROPERTY_SLUG = "22-sevira-avenue-gledswood-hills-nsw-2557";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MOBILE_RE = /^(\+?61|0)4\d{2}\s?\d{3}\s?\d{3}$/;

export default function BrandedSendControl({
  channel,          // "email" | "sms"
  endpoint,         // backend sample endpoint name
  reportType,       // for send-property-report
  branding,         // { colour, agencyId, agentId, agentEmail }
  slug,             // landing_page_slug
  cta = "Send",
  placeholder,
  source = "branded_hub_sample_send",
  compact = false,  // shrink padding for tight contexts
  floatingMessage = false, // render the status message absolutely above the pill so it doesn't push surrounding layout
}) {
  const isSms = channel === "sms";
  const accent = (branding && branding.colour) || "#10b981";
  const solid = agentSolidColour(accent);

  const initial = !isSms && branding && branding.agentEmail ? branding.agentEmail : "";
  const [value, setValue] = useState(initial);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);

  const send = async () => {
    const to = value.trim();
    if (isSms) {
      if (!MOBILE_RE.test(to.replace(/\s/g, ""))) {
        setOk(false);
        setMsg("Enter a valid Australian mobile.");
        return;
      }
    } else if (!EMAIL_RE.test(to)) {
      setOk(false);
      setMsg("Enter a valid email address.");
      return;
    }
    setSending(true);
    setMsg("");
    try {
      let payload;
      if (endpoint === "send-property-report") {
        payload = {
          email: to,
          property_id: SAMPLE_PROPERTY_ID,
          property_slug: SAMPLE_PROPERTY_SLUG,
          agency_id: branding && branding.agencyId,
          agent_id: branding && branding.agentId,
          agent_slug: slug,
          report_type: reportType || "property_update",
          source,
        };
      } else {
        payload = { mobile: to };
      }
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/${endpoint}`,
        payload,
        { "Content-Type": "application/json" }
      );
      const success =
        res?.data?.status === "success" || (res?.status === 200 && !res?.data?.error);
      setOk(success);
      setMsg(
        success
          ? `Sample sent to ${to}.`
          : res?.data?.message || res?.data?.error || "Failed to send sample."
      );
      if (success && isSms) setValue("");
    } catch {
      setOk(false);
      setMsg("Failed to send sample. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const pad = compact ? "0.45rem 0.6rem" : "0.55rem 0.7rem";
  const btnPad = compact ? "0 0.85rem" : "0 1rem";

  return (
    <div style={{ width: "100%", position: "relative" }}>
      {floatingMessage && msg && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            fontSize: "0.75rem",
            color: ok ? "#10b981" : "#dc2626",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {msg}
        </div>
      )}
      <style>{`
        @keyframes vlSpin { to { transform: rotate(360deg); } }
      `}</style>
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          border: `1.5px solid ${solid}`,
          borderRadius: 10,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        <input
          type={isSms ? "tel" : "email"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !sending) send(); }}
          placeholder={placeholder}
          disabled={sending}
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: "0.85rem",
            padding: pad,
            border: "none",
            outline: "none",
            background: "transparent",
          }}
        />
        <button
          onClick={send}
          disabled={sending}
          data-track-event="true"
          style={{
            border: "none",
            background: solid,
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.82rem",
            padding: btnPad,
            cursor: sending ? "default" : "pointer",
            opacity: sending ? 0.7 : 1,
            whiteSpace: "nowrap",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            minWidth: sending ? 56 : undefined,
          }}
        >
          {sending ? (
            <span
              aria-label="Sending"
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                border: "2px solid rgba(255,255,255,0.4)",
                borderTopColor: "#fff",
                animation: "vlSpin 0.7s linear infinite",
                display: "inline-block",
              }}
            />
          ) : (
            cta
          )}
        </button>
      </div>
      {!floatingMessage && msg && (
        <div
          style={{
            marginTop: 4,
            fontSize: "0.75rem",
            color: ok ? "#10b981" : "#dc2626",
          }}
        >
          {msg}
        </div>
      )}
    </div>
  );
}
